import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { historyApi } from "../api/historyApi";
import { individualsApi } from "@/lib/planApi";
import { usersApi as orgUsersApi } from "@/lib/orgApi";
import useAuthStore from "@/store/authStore";
import useAlertsStore from "@/store/alertsStore";
import HistoryFilters from "../components/HistoryFilters";
import HistoryItem from "../components/HistoryItem";
import HistoryEmpty from "../components/HistoryEmpty";
import HistoryPagination from "../components/HistoryPagination";
import ScoringSheet from "../components/ScoringSheet";

const DEBOUNCE_MS = 300;

const HistoryPage = () => {
    const user = useAuthStore((s) => s.user);
    const refreshAlerts = useAlertsStore((s) => s.refreshAlerts);

    const [range, setRange] = useState("all");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [individualId, setIndividualId] = useState(null);
    const [caregiverId, setCaregiverId] = useState(null);
    const [statusFilter, setStatusFilter] = useState("all");
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [individuals, setIndividuals] = useState([]);
    const [caregivers, setCaregivers] = useState([]);

    const [sheetItem, setSheetItem] = useState(null);
    const [sheetOpen, setSheetOpen] = useState(false);
    const [sheetReadOnly, setSheetReadOnly] = useState(false);

    const isSupervisorPlus = user?.role !== "caregiver";
    const debounceRef = useRef();

    // Load individuals + caregivers (once)
    useEffect(() => {
        individualsApi
            .list({ active: true })
            .then((rows) => setIndividuals(rows || []))
            .catch(() => setIndividuals([]));
        if (isSupervisorPlus) {
            orgUsersApi
                .list({ role: "caregiver", active: true })
                .then((rows) => setCaregivers(rows || []))
                .catch(() => setCaregivers([]));
        }
    }, [isSupervisorPlus]);

    // Fetch history whenever filters change
    const fetchHistory = useCallback(() => {
        if (range === "custom") {
            if (!dateFrom || !dateTo) return;
            if (new Date(dateFrom) > new Date(dateTo)) return;
        }
        const params = {
            range,
            status: statusFilter,
            page,
            page_size: pageSize,
        };
        if (range === "custom") {
            params.date_from = dateFrom;
            params.date_to = dateTo;
        }
        if (individualId) params.individual_id = individualId;
        if (caregiverId && isSupervisorPlus) params.caregiver_id = caregiverId;

        setLoading(true);
        historyApi
            .list(params)
            .then((r) => setData(r))
            .catch((err) => {
                toast.error(err?.message || "Could not load history");
                setData(null);
            })
            .finally(() => setLoading(false));
    }, [range, dateFrom, dateTo, individualId, caregiverId, statusFilter, page, pageSize, isSupervisorPlus]);

    useEffect(() => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(fetchHistory, DEBOUNCE_MS);
        return () => clearTimeout(debounceRef.current);
    }, [fetchHistory]);

    // Reset to page 1 on any filter change
    useEffect(() => {
        setPage(1);
    }, [range, dateFrom, dateTo, individualId, caregiverId, statusFilter, pageSize]);

    const handleOpen = (item) => {
        const isMineOrNew =
            item.kind !== "measurement" ||
            !item.caregiver ||
            item.caregiver.id === user?.id;
        setSheetItem({
            task: item.task,
            activity: item.activity,
            outcome: item.outcome,
            individual_id: item.individual.id,
            measurement_id: item.measurement?.id,
            backfill_date:
                item.kind === "backlog" ? item.expected_date : null,
        });
        setSheetReadOnly(!isMineOrNew);
        setSheetOpen(true);
    };

    const clearAll = () => {
        setRange("all");
        setDateFrom("");
        setDateTo("");
        setIndividualId(null);
        setCaregiverId(null);
        setStatusFilter("all");
        setPage(1);
    };

    const items = data?.items || [];
    const counts = data?.counts;
    const pageInfo = data?.page_info || {
        page: 1,
        page_size: pageSize,
        total_items: 0,
        total_pages: 1,
    };

    const rangeLabel = useMemo(() => {
        if (range === "all") return "All time";
        if (range === "custom" && dateFrom && dateTo) {
            return `${dateFrom} → ${dateTo}`;
        }
        const days = parseInt(range, 10);
        if (!isNaN(days)) return `Last ${days} days`;
        return "";
    }, [range, dateFrom, dateTo]);

    return (
        <div
            data-testid="history-page"
            className="mx-auto w-full max-w-4xl px-4 py-6 md:py-8"
        >
            <div className="mb-4 flex items-center gap-3">
                <div>
                    <h1 className="text-2xl font-heading font-black tracking-tight text-slate-900">
                        History
                    </h1>
                    <p className="text-sm text-slate-500">
                        {counts?.total ?? 0} entries · {rangeLabel}
                        {isSupervisorPlus && (
                            <span className="ml-2 inline-block rounded-full bg-brand-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-blue-800">
                                Team view
                            </span>
                        )}
                    </p>
                </div>
            </div>

            <HistoryFilters
                range={range}
                dateFrom={dateFrom}
                dateTo={dateTo}
                individualId={individualId}
                caregiverId={caregiverId}
                statusFilter={statusFilter}
                counts={counts}
                individuals={individuals}
                caregivers={caregivers}
                showCaregiverFilter={isSupervisorPlus}
                onRangeChange={setRange}
                onDateFromChange={setDateFrom}
                onDateToChange={setDateTo}
                onIndividualChange={setIndividualId}
                onCaregiverChange={setCaregiverId}
                onStatusChange={setStatusFilter}
                onClear={clearAll}
            />

            <div className="mt-4 space-y-2">
                {loading && (
                    <div className="text-center text-xs text-slate-500">
                        Loading…
                    </div>
                )}
                {!loading && items.length === 0 && (
                    <HistoryEmpty statusFilter={statusFilter} />
                )}
                {items.map((it, idx) => (
                    <HistoryItem
                        key={`${it.kind}-${it.task.id}-${it.individual.id}-${it.event_at}-${idx}`}
                        item={it}
                        index={idx}
                        showCaregiver={isSupervisorPlus}
                        onOpen={handleOpen}
                    />
                ))}
            </div>

            <HistoryPagination
                page={pageInfo.page}
                pageSize={pageInfo.page_size}
                totalPages={pageInfo.total_pages}
                totalItems={pageInfo.total_items}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
            />

            <ScoringSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                item={sheetItem}
                readOnly={sheetReadOnly}
                onSaved={() => {
                    fetchHistory();
                    refreshAlerts?.();
                }}
            />
        </div>
    );
};

export default HistoryPage;
